import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/20/solid';
import { TrashIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { nextCopyName } from '../../../../utils/duplicateName';
import ModalDelete from '../../../../components/modal-delete/modal-delete';
import { useSelector } from 'react-redux';
import optionApi from '../../../../services/optionApi';
import storeApi from '../../../../services/storeApi';
import { Select } from 'antd';
import { TWButton, ToggleSwitch } from '../../../../components/ui';
import { Tooltip } from 'react-tooltip';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OptionVisibleModal from './option-visible-modal';
import DeleteHidedateModal from './delete-hidedate-modal';
import OptionCaloriesField from '../../../../components/menu/calories/option-calories-field';
import OptionRowMenu from '../../../../components/menu/option-row-menu';
import { CALORIES_MODE } from '../../../../utils/calories';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const inputClass =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400 dark:focus:border-sky-400 dark:focus:ring-sky-400';

export default function OptionGroupUpsertModal({
  show,
  optionGroupForm,
  setOptionGroupForm,
  onClickOptionGroupFormSubmit,
  getOptionGroupList,
  loading,
  setLoading,
  dialogs,
  setDialogs,
  errors,
  setErrors,
}) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });
  const [storeList, setStoreList] = useState([]);
  // Store timezone — hidedate'i product'taki gibi store saatine göre hesaplamak için.
  const storeTimezoneId = storeList[0]?.timezoneid || null;
  const [selectedOptionVisible, setSelectedOptionVisible] = useState({});

  const onOptionGroupFormChange = (val, key) => {
    setOptionGroupForm((prev) => ({ ...prev, [key]: val }));
  };

  const onOptionFormChange = (val, key, index) => {
    if (key === 'visible' && !val) {
      onClickOpenVisibleModal(optionGroupForm.options[index], index);
    } else {
      let newOptionList = [...optionGroupForm.options];
      newOptionList[index][key] = val;
      setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    }
  };

  // An option with a future hidedate is "scheduled to hide" — the switch should
  // read OFF (like products), not stay blue just because visible is still true.
  const isScheduledHidden = (option) =>
    !!option.hidedate && new Date() < new Date(option.hidedate);

  // The options exactly as the server last returned them, kept beside the
  // editable draft. `onOptionFormChange` writes uncommitted edits straight into
  // the draft option, so the visibility auto-save below must not spread it.
  const serverOptionsRef = useRef({});

  // Record each option the first time its id is seen — that is the load, before
  // any keystroke can reach it. Later renders carry the *same* option objects
  // (the draft is edited in place), so a recorded row is never overwritten by a
  // dirty one.
  useEffect(() => {
    (optionGroupForm.options || []).forEach((option) => {
      if (option.id && !serverOptionsRef.current[option.id]) {
        serverOptionsRef.current[option.id] = { ...option };
      }
    });
  }, [optionGroupForm.options]);

  useEffect(() => {
    serverOptionsRef.current = {};
  }, [optionGroupForm.id]);

  // Persist a single option's visible/hidedate immediately so the change is not
  // lost when a user forgets to press Save. Only existing options in a saved
  // group can be upserted standalone; new ones still persist on the group save.
  //
  // The upsert replaces the whole row, so every field this control does not own
  // is sent from the stored row rather than the draft. Spreading the draft would
  // let an unsaved calories/price/name edit ride along on a toggle the user
  // never meant as a Save — and Cancel could no longer take it back.
  const persistOptionVisible = async (option) => {
    if (!option.id || !optionGroupForm.id) return;
    const serverOption = serverOptionsRef.current[option.id];
    if (!serverOption) return;
    const body = {
      ...serverOption,
      visible: option.visible,
      hidedate: option.hidedate,
      optiongroupid: optionGroupForm.id,
      name: serverOption.name ? serverOption.name.trim() : serverOption.name,
    };
    try {
      await optionApi.upsert(body);
      serverOptionsRef.current[option.id] = { ...body };
    } catch (e) {
      console.error('Option visibility auto-save failed', e);
    }
  };

  const applyOption = (option, index) => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: prev.options.map((it, idx) => (idx === index ? option : it)),
    }));
  };

  // Mirrors the product visible behaviour: touching a scheduled switch re-shows
  // the option now; turning OFF opens the hide-duration modal; turning ON shows
  // it. Every path auto-saves.
  const onChangeOptionVisible = (val, index) => {
    const option = optionGroupForm.options[index];
    if (isScheduledHidden(option)) {
      const updated = { ...option, hidedate: '', visible: true };
      applyOption(updated, index);
      persistOptionVisible(updated);
      return;
    }
    if (!val) {
      onClickOpenVisibleModal(option, index);
    } else {
      const updated = { ...option, visible: true, hidedate: '' };
      applyOption(updated, index);
      persistOptionVisible(updated);
    }
  };

  const onClickOpenVisibleModal = (option, index) => {
    setSelectedOptionVisible({ option, index });
    setDialogs((prev) => ({ ...prev, optionVisible: true }));
  };

  const onSelectVisible = async (time) => {
    let { option, index } = selectedOptionVisible;
    option.visible = true;

    // Store timezone kullan. Backend'e timezone offset'li gönder (örn. -08:00),
    // böylece "ne zaman gizlenecek" kararı viewer'ın cihaz saatinden bağımsız olur.
    const tz = storeTimezoneId || 'UTC';
    const base = dayjs().tz(tz);

    if (time === 'tomorrow') {
      option.hidedate = base
        .add(1, 'day')
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    } else if (time === 'forever') {
      option.visible = false;
      option.hidedate = '';
    } else {
      option.hidedate = base
        .add(Number(time), 'hour')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    }

    const newOptionList = optionGroupForm.options.map((item, idx) =>
      idx === index ? option : item,
    );
    setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    await persistOptionVisible(option);
    setDialogs((prev) => ({ ...prev, optionVisible: false }));
    setSelectedOptionVisible({});
  };

  const onClickOpenHidedateModal = (option, val, i) => {
    setSelectedOptionVisible({ option, val, i });
    setDialogs((prev) => ({ ...prev, hidedateDelete: true }));
  };

  const onCloseHidedateModal = () => {
    setDialogs((prev) => ({ ...prev, hidedateDelete: false }));
    setSelectedOptionVisible({});
  };

  const onCloseVisible = () => {
    setDialogs((prev) => ({ ...prev, optionVisible: false }));
  };

  const onSubmitDeleteHideDate = async () => {
    let { option, index } = selectedOptionVisible;
    option.hidedate = null;
    option.visible = true;
    const newOptionList = optionGroupForm.options.map((item, idx) =>
      idx === index ? option : item,
    );
    setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    await persistOptionVisible(option);
    setDialogs((prev) => ({ ...prev, hidedateDelete: false }));
    setSelectedOptionVisible({});
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, name: false, price: false }));
    setOptionGroupForm((prev) => ({
      ...prev,
      name: '',
      decription: '',
      displayorder: '',
      storeids: [],
      min: '',
      max: '',
      allowquantities: false,
      companyid: globalCompany.id,
      options: [],
      productoptiongroups: prev.productoptiongroups,
    }));
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: false }));
  };

  const onClickDeleteOptionGroup = (optionGroup) => {
    setDeletedItem(optionGroup);
    setDialogs((prev) => ({ ...prev, optionGroupDelete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await optionApi.deleteGroupById(deletedItem.id);
    } catch (error) {
    } finally {
      await getOptionGroupList();
      setDialogs((prev) => ({
        ...prev,
        optionGroupDelete: false,
        optionGroupUpsert: false,
      }));
      setLoading((prev) => ({
        ...prev,
        deleteLoading: false,
        upsertLoading: false,
      }));
    }
  };

  const onClickDeleteOption = async (option) => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      if (option.id) {
        await optionApi.deleteById(option.id);
      }
      const newOptionList = optionGroupForm.options.filter(
        (item) => item !== option,
      );
      setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const onClickAddOption = () => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          name: '',
          displayorder:
            prev.options.length > 0
              ? Number(prev.options[prev.options.length - 1].displayorder) + 1
              : 1,
          price: 0,
          visible: true,
          optiongroupid: optionGroupForm.id,
          hidedate: '',
          calories: '',
          caloriesmode: CALORIES_MODE.ADD,
        },
      ],
    }));
  };

  const handleSwap = async (currentIndex, targetIndex) => {
    if (targetIndex < 0 || targetIndex >= optionGroupForm.options?.length)
      return;
    setOptionGroupForm((prevOptionsGroups) => {
      let newOptions = [...prevOptionsGroups.options];
      const tempDisplayOrder = newOptions[currentIndex].displayorder;
      newOptions[currentIndex].displayorder =
        newOptions[targetIndex].displayorder;
      newOptions[targetIndex].displayorder = tempDisplayOrder;
      const tempItem = newOptions[currentIndex];
      newOptions[currentIndex] = newOptions[targetIndex];
      newOptions[targetIndex] = tempItem;
      return { ...prevOptionsGroups, options: newOptions };
    });
  };

  const onDuplicateOption = (i) => {
    const src = optionGroupForm.options[i];
    const siblingNames = optionGroupForm.options.map((o) => o.name);
    const copy = {
      ...src,
      id: undefined,
      name: nextCopyName(src.name, siblingNames),
    };
    setOptionGroupForm((prev) => ({
      ...prev,
      options: [
        ...prev.options.slice(0, i + 1),
        copy,
        ...prev.options.slice(i + 1),
      ],
    }));
  };

  const getStoreList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, storeLoading: true }));
    try {
      const query = `companyid=${globalCompany.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
      }
    } catch (error) {}
    setLoading((prev) => ({ ...prev, storeLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getStoreList();
  }, [getStoreList]);

  if (!show) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto hide-scrollbar px-4 py-4">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {optionGroupForm.id ? 'Edit Group' : 'New Group'}
        </h3>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          {optionGroupForm.id
            ? 'Update modifier group settings and options.'
            : 'Configure a new modifier group for your products.'}
        </p>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Group Name
            </label>
            <input
              value={optionGroupForm.name}
              onChange={(e) => onOptionGroupFormChange(e.target.value, 'name')}
              type="text"
              className={inputClass}
              placeholder="e.g. Size, Toppings"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">Name is required</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Description
              <span className="ml-1 font-normal text-slate-400">
                (optional)
              </span>
            </label>
            <input
              value={optionGroupForm.description}
              onChange={(e) =>
                onOptionGroupFormChange(e.target.value, 'description')
              }
              type="text"
              className={inputClass}
              placeholder="Brief description"
            />
          </div>

          {!loading.storeLoading && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Stores
              </label>
              <Select
                mode="multiple"
                allowClear
                size="middle"
                style={{ width: '100%' }}
                placeholder="Select stores"
                value={optionGroupForm?.storeids}
                onChange={(val) => onOptionGroupFormChange(val, 'storeids')}
                options={storeList.map((x) => ({
                  label: x.name,
                  value: x.id,
                }))}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Min Select
              </label>
              <input
                value={optionGroupForm.min}
                onChange={(e) => onOptionGroupFormChange(e.target.value, 'min')}
                type="number"
                className={inputClass}
                placeholder="0"
              />
              {errors.min && (
                <p className="mt-1 text-xs text-red-600">Required</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Max Select
              </label>
              <input
                value={optionGroupForm.max}
                onChange={(e) => onOptionGroupFormChange(e.target.value, 'max')}
                type="number"
                className={inputClass}
                placeholder="0"
              />
              {errors.max && (
                <p className="mt-1 text-xs text-red-600">Required</p>
              )}
            </div>
          </div>

          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Allow multiple of the same option
              </span>
              <p className="mt-0.5 text-[0.65rem] text-slate-500 dark:text-slate-400">
                Customers can pick one option more than once — each pick counts
                toward Max Select
              </p>
            </div>
            <ToggleSwitch
              checked={!!optionGroupForm.allowquantities}
              onChange={(val) => onOptionGroupFormChange(val, 'allowquantities')}
              ariaLabel="Allow multiple of the same option"
            />
          </div>
        </div>

        <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Options
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {optionGroupForm.options.length} option
                {optionGroupForm.options.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={onClickAddOption}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add
            </button>
          </div>

          {optionGroupForm.options.length > 0 && (
            <div className="mt-3 space-y-2">
              {optionGroupForm.options.map((option, i) => (
                <div
                  key={i}
                  className="rounded-md border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-600 dark:bg-slate-700/30"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col items-center gap-0.5 self-center">
                      <button
                        type="button"
                        onClick={() => handleSwap(i, i - 1)}
                        disabled={i === 0}
                        className={classNames(
                          i === 0 ? 'invisible' : '',
                          'rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-600 dark:hover:text-slate-200',
                        )}
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSwap(i, i + 1)}
                        disabled={i === optionGroupForm.options?.length - 1}
                        className={classNames(
                          i === optionGroupForm.options?.length - 1
                            ? 'invisible'
                            : '',
                          'rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-600 dark:hover:text-slate-200',
                        )}
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1 grid grid-cols-2 gap-2">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Name
                        </label>
                        <input
                          value={option.name}
                          onChange={(e) =>
                            onOptionFormChange(e.target.value, 'name', i)
                          }
                          maxLength={60}
                          type="text"
                          className={inputClass}
                          placeholder="Option name"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Price
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                            $
                          </span>
                          <input
                            value={option.price}
                            onChange={(e) =>
                              onOptionFormChange(e.target.value, 'price', i)
                            }
                            type="number"
                            className={classNames(inputClass, '!pl-6')}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="col-span-1 flex items-end gap-2">
                        <div>
                          <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Visible
                          </label>
                          <Switch
                            checked={option.visible && !isScheduledHidden(option)}
                            onChange={(e) => onChangeOptionVisible(e, i)}
                            className={classNames(
                              option?.visible && !isScheduledHidden(option)
                                ? 'bg-blue-600 dark:bg-sky-500'
                                : 'bg-slate-300 dark:bg-slate-500',
                              'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={classNames(
                                option?.visible && !isScheduledHidden(option)
                                  ? 'translate-x-4'
                                  : 'translate-x-0',
                                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition',
                              )}
                            />
                          </Switch>
                        </div>
                        {option.hidedate &&
                          new Date() < new Date(option.hidedate) && (
                            <>
                              <Tooltip id={`hidedate-${i}`} />
                              <CalendarDaysIcon
                                onClick={() =>
                                  onClickOpenHidedateModal(option, true, i)
                                }
                                data-tooltip-id={`hidedate-${i}`}
                                data-tooltip-content={`Hidden until ${dayjs(
                                  option.hidedate,
                                  'YYYY-MM-DD hh:mm A',
                                ).format('MM/DD/YYYY hh:mm A')}`}
                                className="h-4 w-4 cursor-pointer text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
                              />
                            </>
                          )}
                      </div>
                      {/* Option calories hidden 2026-07-27 (main-option-only
                          scope-down). Data round-trips via {...item} on save.
                          Re-enable: restore <OptionCaloriesField/> here. */}
                    </div>

                    <div className="mt-5">
                      <OptionRowMenu
                        onDuplicate={() => onDuplicateOption(i)}
                        onDelete={() => onClickDeleteOption(option)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/80">
        <div className="grid grid-cols-12 gap-3">
          {optionGroupForm.id && (
            <TWButton
              variant="danger"
              icon={TrashIcon}
              disabled={loading.deleteLoading}
              loading={loading.deleteLoading}
              onClick={() => onClickDeleteOptionGroup(optionGroupForm)}
              className="col-span-2 w-full"
            />
          )}
          <TWButton
            variant="default"
            label="Cancel"
            disabled={loading.upsertLoading}
            onClick={onCloseUpsert}
            className={
              optionGroupForm.id ? 'col-span-5 w-full' : 'col-span-6 w-full'
            }
          />
          <TWButton
            variant="primary"
            label={optionGroupForm.id ? 'Save' : 'Create'}
            disabled={loading.upsertLoading}
            loading={loading.upsertLoading}
            onClick={onClickOptionGroupFormSubmit}
            className={
              optionGroupForm.id ? 'col-span-5 w-full' : 'col-span-6 w-full'
            }
          />
        </div>
      </div>

      <OptionVisibleModal
        onSelectVisible={onSelectVisible}
        show={dialogs.optionVisible}
        loading={loading.visibleLoading}
        errors={errors}
        onCloseVisible={onCloseVisible}
      />
      <DeleteHidedateModal
        show={dialogs.hidedateDelete}
        loading={loading.upsertLoading}
        onClose={onCloseHidedateModal}
        onSubmit={onSubmitDeleteHideDate}
      />
      <ModalDelete
        title="Modifier Group"
        text={`Are you sure you want to delete "${deletedItem.name}"? All associated data will be permanently removed.`}
        loading={loading.deleteLoading}
        open={dialogs.optionGroupDelete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, optionGroupDelete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedItem}
      />
    </div>
  );
}
